import { inject, Service } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { ChecklistHttpService } from './checklist-http.service';
import { ChecklistStoreService } from './checklist-store.service';
import { NetworkStatusService } from './offline/network-status.service';
import { OfflineStorageService } from './offline/offline-storage.service';

@Service()
export class ChecklistWriteSenderService {
  private readonly store = inject(ChecklistStoreService);
  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly offlineStorageService = inject(OfflineStorageService);

  // True when the write went online, and false when queued for sync or, for an isolated preview, kept local.
  async sendOrQueue<T extends keyof ChecklistHttpService>(
    checklistId: string,
    method: ChecklistHttpService[T],
    ...params: Parameters<ChecklistHttpService[T]>
  ): Promise<boolean> {
    if (this.store.isIsolatedPreview(checklistId)) {
      return false;
    }
    if (!this.networkStatusService.online()) {
      await this.offlineStorageService.addAction<T>(method, ...params);
      return false;
    }

    const send = method as (...args: unknown[]) => Observable<unknown>;
    await firstValueFrom(send.apply(this.checklistHttpService, params)).catch(
      error => {
        console.error(error);
        throw error;
      }
    );
    return true;
  }

  isNonQueueableHttpFailure(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status >= 400 &&
      error.status < 500
    );
  }
}
